/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState, useCallback } from "react";
import { WebpageSelector } from "components/webpage-selector";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { isStringNotEmpty, isGuid } from "utils/empty-exists";
import { AddWebpageButton } from "./add-webpage-button";
import { WebpageDisplay } from "./webpage-display";
import { cloneObjectsArray } from "utils/array";

const CardWebpageSelector = React.memo((props) => {
    const { card, index, cardsList, cardsListRef, updateCards } = props;
    const { token } = useAuth();
    
    const webpageGuid = card?.webpage || "";
    const [localWebpage, setLocalWebpage] = useState();

    // Load webpage from guid on mount and when guid changes
    useEffect(() => {
        const loadWebpage = async () => {
            if (isStringNotEmpty(webpageGuid) && isGuid(webpageGuid)) {
                try {
                    const webpageObj = await SimpliCityAdminSDK.webpage.findOne(token, webpageGuid);
                    setLocalWebpage(webpageObj);
                } catch (err) {
                    setLocalWebpage(undefined);
                }
            } else {
                setLocalWebpage(undefined);
            }
        };
        loadWebpage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webpageGuid, token]);

    const handleSelectWebpage = useCallback((selectedWebpage) => {
        setLocalWebpage(selectedWebpage);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                webpage: selectedWebpage?.guid || "",
            };
            updateCards(updatedCards);
        }
    }, [cardsListRef, cardsList, updateCards, index, card]);

    const handleWebpageRemoved = useCallback(() => {
        setLocalWebpage(undefined);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                webpage: "",
            };
            updateCards(updatedCards);
        }
    }, [cardsListRef, cardsList, updateCards, index, card]);

    const WebpageDisplayWrapper = useCallback((displayProps) => {
        return (
            <WebpageDisplay
                {...displayProps}
                webpage={displayProps.webpage || localWebpage}
                card={card}
                index={index}
                cardsList={cardsList}
                cardsListRef={cardsListRef}
                updateCards={updateCards}
            />
        );
    }, [localWebpage, card, index, cardsList, cardsListRef, updateCards]);

    return (
        <WebpageSelector
            idx={index}
            webpage={localWebpage}
            onSelectWebpage={handleSelectWebpage}
            onWebpageRemoved={handleWebpageRemoved}
            AddComponent={AddWebpageButton}
            DisplayComponent={WebpageDisplayWrapper}
        />
    );
}, (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Excluded title and description to prevent rerenders when only those change
    return (
        prevProps.index === nextProps.index &&
        prevProps.card?.webpage === nextProps.card?.webpage &&
        prevProps.card?.banner_image?.image === nextProps.card?.banner_image?.image
    );
});

CardWebpageSelector.displayName = "CardWebpageSelector";
export { CardWebpageSelector };
